import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Autocomplete,
  Box,
  Chip,
  createFilterOptions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTenantStore } from '../../../stores/tenant.store';
import { useCurrentMembership } from '../../../hooks/use-current-membership';
import { useCurrentPersonId } from '../../../hooks/use-current-person-id';
import {
  buildSectionRows,
  defaultTermCandidates,
  fetchCatalogNames,
  fetchFacultyTermIds,
  fetchPickerTerms,
  fetchSectionsOverview,
  fetchSectionTermId,
  fetchTermSections,
  termHasSections,
  type SectionRow,
} from './section-picker.data';

// Autocomplete: match on both the course name and the teacher's name.
const sectionFilter = createFilterOptions<SectionRow>({
  stringify: (s) => `${s.courseName} ${s.facultyName ?? ''} ${s.programName ?? ''}`,
});

interface SectionPickerProps {
  /** Selected section id (controlled). */
  value: string;
  /** Called with the new section id ('' when cleared). */
  onChange: (sectionId: string) => void;
}

/** How long the shared reads stay fresh between the screens that use the picker. */
const STALE_MS = 60_000;

/**
 * Scalable section selector shared by the LMS (Curso) and Materials pages:
 * a Period filter (defaults to the active term, scopes the list) + a searchable
 * section autocomplete (matches course OR teacher). Owns its own section/term
 * loading; the parent only holds the selected section id.
 *
 * It reads ONLY the selected period's sections (see section-picker.data.ts).
 */
export function SectionPicker({ value, onChange }: SectionPickerProps) {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const membership = useCurrentMembership();
  const facultyPersonId = useCurrentPersonId();
  const tenantId = currentTenantId ?? '';

  const isFaculty =
    !!membership?.roles?.includes('faculty') &&
    !membership?.roles?.includes('tenantAdmin') &&
    !membership?.roles?.includes('registrar');
  // `facultyId` es un id de PERSONA, no el uid de Auth. Si la persona no se pudo
  // resolver, NO se filtra — mejor mostrar de más que dejar a alguien mirando
  // una lista vacía sin explicación.
  const facultyScope = isFaculty && facultyPersonId ? facultyPersonId : null;

  const [selectedTermId, setSelectedTermId] = useState<string>('');

  const termsQ = useQuery({
    queryKey: ['picker-terms', tenantId],
    enabled: !!tenantId,
    queryFn: () => fetchPickerTerms(tenantId),
    staleTime: STALE_MS,
  });
  const catalogQ = useQuery({
    queryKey: ['picker-catalog', tenantId],
    enabled: !!tenantId,
    queryFn: () => fetchCatalogNames(tenantId),
    staleTime: STALE_MS,
  });
  // A teacher only sees the periods where they teach (their sections are few).
  const facultyTermsQ = useQuery({
    queryKey: ['picker-faculty-terms', tenantId, facultyScope],
    enabled: !!tenantId && !!facultyScope,
    queryFn: () => fetchFacultyTermIds(tenantId, facultyScope!),
    staleTime: STALE_MS,
  });

  const terms = useMemo(() => {
    const all = termsQ.data ?? [];
    if (!facultyScope) return all;
    const mine = facultyTermsQ.data;
    return mine ? all.filter((tm) => mine.has(tm.id)) : [];
  }, [termsQ.data, facultyScope, facultyTermsQ.data]);

  // Default the period once ready. A deep link (/curso/:sectionId) opens on
  // that section's period; otherwise the active term (in progress → open →
  // newest) — the first of them that has sections.
  useEffect(() => {
    if (selectedTermId || terms.length === 0 || !tenantId) return;
    let cancelled = false;
    (async () => {
      if (value) {
        const own = await fetchSectionTermId(tenantId, value).catch(() => null);
        if (own && terms.some((tm) => tm.id === own)) {
          if (!cancelled) setSelectedTermId(own);
          return;
        }
      }
      const candidates = defaultTermCandidates(terms);
      // A teacher's periods all have sections; for staff, skip empty ones (few reads).
      for (const id of facultyScope ? candidates.slice(0, 1) : candidates.slice(0, 6)) {
        if (facultyScope || (await termHasSections(tenantId, id).catch(() => false))) {
          if (!cancelled) setSelectedTermId(id);
          return;
        }
      }
      if (!cancelled) setSelectedTermId(candidates[0] ?? '');
    })();
    return () => {
      cancelled = true;
    };
  }, [terms, selectedTermId, value, tenantId, facultyScope]);

  // Sections of the selected period (the autocomplete scope), with their labels.
  const sectionsQ = useQuery({
    queryKey: ['picker-sections', tenantId, selectedTermId, facultyScope],
    enabled: !!tenantId && !!selectedTermId && !!catalogQ.data,
    staleTime: STALE_MS,
    queryFn: async (): Promise<SectionRow[]> => {
      const sections = await fetchTermSections(tenantId, selectedTermId, facultyScope);
      const live = sections.filter((s) => s['status'] !== 'cancelled');
      // Docente e inscritos, del servidor (PR 3A). Si falla, el selector igual
      // sirve para elegir: se ve la sección sin docente ni conteo.
      const overview = await fetchSectionsOverview(
        tenantId,
        live.map((s) => s.id),
      ).catch(() => ({ facultyNames: new Map<string, string>(), enrolled: new Map() }));
      // `buildSectionRows` nombra por id de docente; el servidor, por sección.
      const facultyNames = new Map<string, string>();
      for (const s of live) {
        const fid = s['facultyId'] as string | undefined;
        const name = overview.facultyNames.get(s.id);
        if (fid && name) facultyNames.set(fid, name);
      }
      return buildSectionRows({
        sections: live,
        catalog: catalogQ.data!,
        termNames: new Map(terms.map((tm) => [tm.id, tm.name])),
        facultyNames,
        enrolled: overview.enrolled,
      });
    },
  });
  const visibleSections = useMemo(() => sectionsQ.data ?? [], [sectionsQ.data]);

  // Keep the selection valid for the period: clear on switch, auto-pick if unique.
  useEffect(() => {
    if (!selectedTermId || !sectionsQ.data) return;
    if (value && visibleSections.some((s) => s.id === value)) return;
    onChange(visibleSections.length === 1 ? visibleSections[0]!.id : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTermId, sectionsQ.data]);

  const noTerms = termsQ.isSuccess && terms.length === 0;

  return (
    <>
      {/* Period filter — defaults to the active term, scopes the section list */}
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel>{t('lms.section.period')}</InputLabel>
        <Select
          value={terms.some((tm) => tm.id === selectedTermId) ? selectedTermId : ''}
          label={t('lms.section.period')}
          onChange={(e) => setSelectedTermId(e.target.value)}
        >
          {noTerms && (
            <MenuItem value="" disabled>
              {isFaculty ? t('lms.section.emptyFaculty') : t('lms.section.emptyAdmin')}
            </MenuItem>
          )}
          {terms.map((tm) => (
            <MenuItem key={tm.id} value={tm.id}>
              {tm.name}
              {tm.status === 'in_progress' ? ` · ${t('lms.section.activeTag')}` : ''}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Searchable section picker — scoped to the selected period */}
      <Autocomplete<SectionRow>
        size="small"
        sx={{ minWidth: 320, flex: 1 }}
        options={visibleSections}
        value={visibleSections.find((s) => s.id === value) ?? null}
        onChange={(_, val) => onChange(val?.id ?? '')}
        getOptionLabel={(s) => s.courseName}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        filterOptions={sectionFilter}
        loading={sectionsQ.isFetching}
        noOptionsText={
          noTerms
            ? isFaculty
              ? t('lms.section.emptyFaculty')
              : t('lms.section.emptyAdmin')
            : t('lms.section.noneInPeriod')
        }
        renderInput={(params) => (
          <TextField
            // MUI render params spread trips exactOptionalPropertyTypes; cast is the standard escape.
            {...(params as unknown as React.ComponentProps<typeof TextField>)}
            size="small"
            label={t('lms.section.label')}
            placeholder={t('lms.section.search')}
          />
        )}
        renderOption={({ key: _key, ...optionProps }, s) => (
          <Box component="li" key={s.id} {...optionProps}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" component="span">
                  {s.courseName}
                </Typography>
                {s.facultyName && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="span"
                    sx={{ ml: 1 }}
                  >
                    · {s.facultyName}
                  </Typography>
                )}
                {/* El programa en su propia línea: es lo que distingue dos
                    cursos casi homónimos de peldaños distintos. */}
                {s.programName && (
                  <Typography variant="caption" color="text.secondary" display="block" noWrap>
                    {s.programName}
                  </Typography>
                )}
              </Box>
              <Chip
                size="small"
                label={t('lms.section.enrolled', { count: s.enrolledCount })}
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            </Stack>
          </Box>
        )}
      />
    </>
  );
}
